(() => {
  const SCRIPT_VERSION = '2026-09-16-final-pass-v1';

  function boot() {
    requestAnimationFrame(() => {
      applyAll();
      setTimeout(applyAll, 120);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => setTimeout(applyAll, 40));
  document.addEventListener('change', event => {
    if (event.target?.closest?.('product-info.it-jewellery-pdp')) {
      setTimeout(applyAll, 70);
    }
  }, true);

  function applyAll() {
    document.querySelectorAll('product-info.it-jewellery-pdp').forEach(applyProduct);
  }

  function applyProduct(root) {
    const sectionId = root.dataset.section || '';
    const context = readContext(sectionId, root);
    const family = detectFamily(context.productType, context.title);
    root.dataset.itJewelleryFamily = family;
    root.dataset.itFinalPass = SCRIPT_VERSION;

    removeDuplicateSupport();
    polishVariantPickers(root, family, context);
    sanitizeProductOverview(root, family, context);
    sanitizeSpecs(root, family);
    rewriteAssuranceContent(root, family);
    polishConfidenceSuite(family);
  }

  function readContext(sectionId, root) {
    const node = document.getElementById(`ItJewelleryFinalContext-${sectionId}`);
    let data = {};
    try { data = JSON.parse(node?.textContent || '{}'); } catch (_) {}
    return {
      productType: String(data.productType || ''),
      title: String(data.title || root.querySelector('h1')?.textContent || ''),
      metal: String(data.metal || ''),
      purity: String(data.purity || ''),
      mainStone: String(data.mainStone || '')
    };
  }

  function detectFamily(productType, title) {
    const type = productType.toLowerCase();
    const text = `${productType} ${title}`.toLowerCase();
    if (/earring/.test(type)) return 'earring';
    if (/bracelet|bangle/.test(type)) return 'bracelet';
    if (/ring|wedding band/.test(type)) return 'ring';
    if (/pendant|charm/.test(type)) return 'pendant';
    if (/chain|necklace/.test(type)) return 'chain';
    if (/earring/.test(text)) return 'earring';
    if (/bracelet|bangle/.test(text)) return 'bracelet';
    if (/ring|wedding band/.test(text)) return 'ring';
    if (/pendant|cross|charm/.test(text)) return 'pendant';
    if (/chain|necklace/.test(text)) return 'chain';
    return 'jewellery';
  }

  function removeDuplicateSupport() {
    document.querySelectorAll('[data-it-et-support-v01], .it-et-support-v01').forEach(node => {
      if (node.tagName === 'STYLE') return;
      node.remove();
    });
  }

  function polishVariantPickers(root, family, context) {
    const fieldsets = [...root.querySelectorAll('[data-it-variant-zone] variant-selects fieldset')];

    fieldsets.forEach((fieldset, index) => {
      const legend = fieldset.querySelector('legend');
      if (!legend) return;
      const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
      const values = radios.map(input => String(input.value || '').trim()).filter(Boolean);
      const raw = String((legend.textContent || '').split(':')[0] || '').trim();
      const rawLower = raw.toLowerCase();

      if (isSyntheticOption(rawLower, values)) {
        fieldset.hidden = true;
        fieldset.classList.add('it-final-synthetic-option');
        fieldset.setAttribute('aria-hidden', 'true');
        return;
      }

      if (family === 'chain' && isChainMatrix(values)) {
        installChainPicker(root, fieldset, values, context);
        return;
      }

      const displayName = optionDisplayName(raw, family, context.title);
      setLegendText(legend, displayName);

      if (/metal|material/i.test(raw) || /metal/i.test(displayName)) {
        fieldset.classList.add('it-metal-options', 'it-final-metal-options');
        radios.forEach(input => decorateMetalLabel(fieldset, input, context.title));
      }

      if (/size/i.test(displayName)) fieldset.classList.add('it-size-options', 'it-final-size-options');
      if (/length/i.test(displayName)) fieldset.classList.add('it-final-length-options');
      if (/stone|gem|colour|color/i.test(displayName)) fieldset.classList.add('it-final-stone-options');
      fieldset.dataset.itFinalOptionIndex = String(index);
    });
  }

  function isSyntheticOption(name, values) {
    if (values.length !== 1) return false;
    const value = values[0].toLowerCase();
    if (name === 'default' && value === 'default') return true;
    if (name === 'title' && value === 'default title') return true;
    return false;
  }

  function optionDisplayName(raw, family, title) {
    const n = raw.toLowerCase().trim();
    if (n === 'color') return 'Colour';
    if (family === 'ring' && (n === 'size' || /ring\s*size/.test(n))) return 'Ring size';
    if (family === 'bracelet' && n === 'size') return 'Bracelet size';
    if (family === 'bracelet' && n === 'length') return 'Bracelet length';
    if (family === 'chain' && n === 'length') return 'Chain length';
    if (/metal|material/.test(n)) return 'Metal';
    if (/stone|gem/.test(n) && /colour|color|type/.test(n)) return 'Stone / colour';
    if (n === 'weight' && /stone|onyx|malachite|agate|pearl|diamond|ruby|sapphire|emerald|topaz|amethyst|opal|turquoise|garnet|quartz|zircon|moissanite/i.test(title)) return 'Stone / colour';
    return raw;
  }

  function setLegendText(legend, text) {
    if (!legend) return;
    const selected = legend.querySelector('[data-selected-value], .selected-value');
    [...legend.childNodes].forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) node.remove();
    });
    legend.insertBefore(document.createTextNode(text), legend.firstChild || null);
    if (selected) selected.remove();
  }

  function decorateMetalLabel(fieldset, input, title) {
    const label = fieldset.querySelector(`label[for="${cssEscape(input.id)}"]`);
    if (!label) return;
    const display = inferMetalDisplay(input.value, title);
    let tone = label.querySelector('.it-metal-tone');
    if (!tone) {
      tone = document.createElement('span');
      tone.className = 'it-metal-tone';
      tone.setAttribute('aria-hidden', 'true');
      label.prepend(tone);
    }
    tone.style.setProperty('--it-metal-tone', toneFor(display));

    let copy = label.querySelector('.it-final-metal-copy');
    if (!copy) {
      copy = document.createElement('span');
      copy.className = 'it-final-metal-copy';
      [...label.childNodes].forEach(node => {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) node.remove();
      });
      label.append(copy);
    }
    copy.textContent = display;
  }

  function inferMetalDisplay(value, title) {
    const raw = String(value || '').trim();
    if (!/gold/i.test(raw) || /yellow|white|rose/i.test(raw)) return raw;
    const titleLower = String(title || '').toLowerCase();
    if (titleLower.includes('rose gold')) return raw.replace(/gold/i, 'Rose Gold');
    if (titleLower.includes('white gold')) return raw.replace(/gold/i, 'White Gold');
    if (titleLower.includes('yellow gold')) return raw.replace(/gold/i, 'Yellow Gold');
    return raw;
  }

  function isChainMatrix(values) {
    if (values.length < 4) return false;
    const matches = values.filter(value => parseChainValue(value)).length;
    return matches / values.length >= 0.8;
  }

  function parseChainValue(value) {
    const text = String(value || '').trim();
    const match = text.match(/^\s*(\d+(?:\.\d+)?)\s*(?:"|in|inch|inches)\s*\/\s*(\d+(?:\.\d+)?)\s*mm\s*$/i);
    if (!match) return null;
    return {
      raw: text,
      length: `${match[1]}\"`,
      lengthNumber: Number(match[1]),
      gauge: `${match[2]}mm`,
      gaugeNumber: Number(match[2])
    };
  }

  function installChainPicker(root, fieldset) {
    fieldset.classList.add('it-final-chain-native');
    fieldset.hidden = true;
    fieldset.setAttribute('aria-hidden', 'true');

    const radios = [...fieldset.querySelectorAll('input[type="radio"]')];
    const parsed = radios.map(input => ({ input, ...parseChainValue(input.value) })).filter(item => item.raw);
    if (!parsed.length) return;

    let proxy = fieldset.previousElementSibling;
    if (!proxy?.matches?.('[data-it-chain-picker]')) {
      proxy = document.createElement('div');
      proxy.className = 'it-chain-picker';
      proxy.dataset.itChainPicker = 'true';
      fieldset.before(proxy);
    }

    const checked = parsed.find(item => item.input.checked) || parsed.find(item => !isRadioUnavailable(item.input)) || parsed[0];
    const gauges = [...new Map(parsed.slice().sort((a, b) => a.gaugeNumber - b.gaugeNumber).map(item => [item.gauge, item])).values()];
    const activeGauge = checked?.gauge || gauges[0]?.gauge;
    const lengths = parsed
      .filter(item => item.gauge === activeGauge)
      .sort((a, b) => a.lengthNumber - b.lengthNumber);

    proxy.innerHTML = '';
    const head = document.createElement('div');
    head.className = 'it-chain-picker__head';
    head.innerHTML = '<span>Choose length &amp; gauge</span><small>Pick a gauge, then choose an available length.</small>';

    const gaugeGroup = document.createElement('div');
    gaugeGroup.className = 'it-chain-picker__group';
    gaugeGroup.innerHTML = '<div class="it-chain-picker__label">Gauge</div>';
    const gaugeButtons = document.createElement('div');
    gaugeButtons.className = 'it-chain-picker__buttons it-chain-picker__buttons--gauge';

    gauges.forEach(gaugeEntry => {
      const availableForGauge = parsed.some(item => item.gauge === gaugeEntry.gauge && !isRadioUnavailable(item.input));
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'it-chain-choice';
      button.textContent = gaugeEntry.gauge;
      button.disabled = !availableForGauge;
      button.classList.toggle('is-selected', gaugeEntry.gauge === activeGauge);
      button.addEventListener('click', () => {
        const sameLength = parsed.find(item => item.gauge === gaugeEntry.gauge && item.length === checked?.length && !isRadioUnavailable(item.input));
        const target = sameLength || parsed.find(item => item.gauge === gaugeEntry.gauge && !isRadioUnavailable(item.input));
        if (target) target.input.click();
      });
      gaugeButtons.append(button);
    });
    gaugeGroup.append(gaugeButtons);

    const lengthGroup = document.createElement('div');
    lengthGroup.className = 'it-chain-picker__group it-chain-picker__group--length';
    lengthGroup.innerHTML = '<div class="it-chain-picker__label">Length</div>';
    const lengthButtons = document.createElement('div');
    lengthButtons.className = 'it-chain-picker__buttons it-chain-picker__buttons--length';
    lengths.forEach(item => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'it-chain-choice';
      button.textContent = item.length;
      button.disabled = isRadioUnavailable(item.input);
      button.classList.toggle('is-selected', item.input.checked);
      button.addEventListener('click', () => item.input.click());
      lengthButtons.append(button);
    });
    lengthGroup.append(lengthButtons);

    const selected = document.createElement('div');
    selected.className = 'it-chain-picker__selected';
    selected.innerHTML = `<span>Selected</span><strong>${escapeHtml(checked?.length || '')} · ${escapeHtml(checked?.gauge || '')}</strong>`;

    proxy.append(head, gaugeGroup, lengthGroup, selected);
  }

  function isRadioUnavailable(input) {
    return Boolean(input.disabled || input.classList.contains('disabled') || input.getAttribute('aria-disabled') === 'true');
  }

  function sanitizeProductOverview(root, family, context) {
    const details = root.querySelector('.it-jewellery-details');
    if (!details) return;
    let section = details.querySelector('.it-piece-summary');
    let paragraph = section?.querySelector('p:last-child');
    const currentText = String(paragraph?.textContent || '').replace(/\s+/g, ' ').trim();

    if (!section && !currentText) {
      section = document.createElement('section');
      section.className = 'it-piece-summary it-final-generated-summary';
      section.innerHTML = '<p class="it-section-kicker">ABOUT THIS PIECE</p><h2>Product overview</h2><p></p>';
      details.prepend(section);
      paragraph = section.querySelector('p:last-child');
    }

    if (!paragraph) return;
    if (!currentText || isBadOverview(currentText)) {
      paragraph.textContent = buildFallbackOverview(family, context);
      section.dataset.itFinalSummary = 'generated';
    }
  }

  function isBadOverview(text) {
    const pipes = (text.match(/\|/g) || []).length;
    const codeTokens = text.match(/\b[A-Z]{2,}\d[A-Z0-9-]*(?:-\d+(?:\.\d+)?)?\b/g) || [];
    const modelLed = /^\s*(?:model|sku|reference|ref\.?)[\s:]/i.test(text);
    const codeHeavy = codeTokens.length >= 4 && codeTokens.join(' ').length / Math.max(text.length, 1) > 0.22;
    return pipes >= 3 || (modelLed && codeTokens.length >= 2) || codeHeavy;
  }

  function buildFallbackOverview(family, context) {
    const material = materialPhrase(context);
    switch (family) {
      case 'chain':
        return `Crafted in ${material}, this chain is presented with a clean, versatile jewellery profile. Choose your preferred length and gauge from the available combinations above; the selected option determines the exact specification supplied.`;
      case 'pendant':
        return `Crafted in ${material}, this pendant is presented with a considered jewellery profile and an easy-to-wear finish. Key dimensions, stone details and other specifications are shown below where available.`;
      case 'ring':
        return `Crafted in ${material}, this ring balances proportion, finish and everyday wearability. Select the available size or metal option above where applicable; the exact specifications are shown below.`;
      case 'earring':
        return `Crafted in ${material}, these earrings are presented with a refined, wearable finish. Metal, stone and construction details are shown below where available, with specialist guidance available from our Kensington team.`;
      case 'bracelet':
        return `Crafted in ${material}, this bracelet is designed with a clean, wearable profile and attention to finish and proportion. Available size, length or metal options are shown above, with the exact specifications listed below.`;
      default:
        return `Crafted in ${material}, this piece is selected for its materials, finish and wearable design. The key specifications shown below reflect the product information available for this listing.`;
    }
  }

  function materialPhrase(context) {
    const metal = context.metal.trim();
    const purity = context.purity.trim();
    if (metal && purity && !metal.toLowerCase().includes(purity.toLowerCase())) return `${purity} ${metal.toLowerCase()}`;
    if (metal) return metal.toLowerCase();
    return 'the stated precious metal';
  }

  function sanitizeSpecs(root, family) {
    const table = root.querySelector('.it-cro-specs .it-spec-table');
    if (!table) return;
    const body = table.tBodies[0] || table;
    [...body.querySelectorAll('tr')].forEach(row => {
      const key = String(row.querySelector('th')?.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const value = String(row.querySelector('td')?.textContent || '').replace(/\s+/g, ' ').trim();
      if ((/main stone|stone 1 type|stone type/.test(key) && /^no stone$/i.test(value)) ||
          (/approx\.? weight|average weight/.test(key) && ((value.match(/\|/g) || []).length >= 2 || (value.match(/\bg\b/gi) || []).length >= 4))) {
        row.remove();
        return;
      }
      if (family === 'chain' && /^(necklace length|length|gauge)$/.test(key)) row.remove();
    });

    body.querySelectorAll('tr[data-it-chain-spec]').forEach(row => row.remove());
    if (family !== 'chain') return;

    const selected = currentChainSelection(root);
    if (!selected) return;
    appendSpecRow(body, 'Length', selected.length, 'length');
    appendSpecRow(body, 'Gauge', selected.gauge, 'gauge');
  }

  function currentChainSelection(root) {
    const radios = [...root.querySelectorAll('[data-it-variant-zone] input[type="radio"]')];
    const input = radios.find(radio => radio.checked && parseChainValue(radio.value));
    return input ? parseChainValue(input.value) : null;
  }

  function appendSpecRow(body, key, value, id) {
    const row = document.createElement('tr');
    row.dataset.itChainSpec = id;
    const th = document.createElement('th');
    th.scope = 'row';
    th.textContent = key;
    const td = document.createElement('td');
    td.textContent = value;
    row.append(th, td);
    body.append(row);
  }

  function rewriteAssuranceContent(root) {
    const why = root.querySelector('.it-jewellery-why');
    if (!why) return;
    const heading = why.querySelector('h2');
    const lead = why.querySelector('.it-jewellery-why__lead');
    if (heading) heading.textContent = 'Why buy from Imperial Time';
    if (lead) lead.textContent = 'Selected and professionally inspected in Kensington, with clear product information, secure fulfilment and specialist support before and after purchase.';

    const content = [
      ['Inspected before dispatch', 'Materials, construction, finish and overall presentation are checked before your order leaves us.'],
      ['Clear product information', 'Key specifications, imagery and available options are presented to help you make an informed choice.'],
      ['Secure fulfilment', 'Orders are carefully packed and sent using an appropriately insured, tracked service.'],
      ['Specialist support', 'Ask our Kensington team about sizing, product details, additional imagery or a showroom viewing.']
    ];
    [...why.querySelectorAll('.it-jewellery-why__grid article')].forEach((card, index) => {
      const item = content[index];
      if (!item) return;
      const h3 = card.querySelector('h3');
      const p = card.querySelector('p');
      if (h3) h3.textContent = item[0];
      if (p) p.textContent = item[1];
    });

    const faqHead = root.querySelector('.it-jewellery-faq__head h2');
    const faqIntro = root.querySelector('.it-jewellery-faq__head > p:last-child');
    if (faqHead) faqHead.textContent = 'Service, delivery & aftercare';
    if (faqIntro) faqIntro.textContent = 'The practical details that matter before and after you order.';
  }

  function polishConfidenceSuite(family) {
    const suite = document.querySelector('[data-it-jewellery-confidence-suite]');
    if (!suite) return;
    const story = suite.querySelector('.it-jewellery-confidence-story > p');
    if (story) story.textContent = 'Every jewellery piece offered by Imperial Time is professionally inspected before sale. We review materials, construction, condition and presentation, and check the product details shown on the listing before dispatch.';
    const points = [...suite.querySelectorAll('.it-jewellery-confidence-points span')];
    if (points[1]) points[1].textContent = 'Materials & construction reviewed';
    const consultation = suite.querySelector('.it-jewellery-consultation-copy > p:last-child');
    if (consultation) consultation.textContent = consultationCopy(family);
  }

  function consultationCopy(family) {
    switch (family) {
      case 'chain': return 'Before you order, our Kensington team can help you compare length, gauge, materials and the available specification for this chain.';
      case 'ring': return 'Before you order, our Kensington team can help you review sizing, materials, stone details and the available specification for this ring.';
      case 'earring': return 'Before you order, our Kensington team can help you review materials, stone details, dimensions and the available specification for these earrings.';
      case 'bracelet': return 'Before you order, our Kensington team can help you review sizing or length, materials, dimensions and the available specification for this bracelet.';
      case 'pendant': return 'Before you order, our Kensington team can help you review materials, dimensions, stone details and the available specification for this pendant.';
      default: return 'Before you order, our Kensington team can help you review the materials, dimensions, condition, sizing and purchase options shown on this listing.';
    }
  }

  function toneFor(value) {
    const v = String(value || '').toLowerCase();
    if (/rose/.test(v)) return '#c99b8c';
    if (/yellow|gold/.test(v)) return '#d5b55f';
    if (/white|silver|platinum/.test(v)) return '#d8d9d6';
    if (/black|onyx/.test(v)) return '#222';
    if (/blue|sapphire/.test(v)) return '#244e8d';
    if (/green|malachite|emerald/.test(v)) return '#2e6a4b';
    if (/red|ruby/.test(v)) return '#9a2f39';
    if (/pink/.test(v)) return '#d7a4ad';
    return '#d8d5cf';
  }

  function cssEscape(value) {
    return window.CSS?.escape ? CSS.escape(String(value)) : String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function escapeHtml(value) {
    return String(value || '').replace(/[&<>'"]/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[char]));
  }
})();
